import React, { useRef, useState } from 'react';
import { Button } from '@alpha-recycling/component-library';
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import { isEqual } from 'lodash';

import { withAlphamartIntlProvider } from 'components/shared/AlphamartIntlProvider';
import { Details } from 'components/shared/Details';
import { LoadableContent } from 'components/shared/Loader';
import { SortableItem, SortableList } from 'components/shared/SortableList/SortableList';
import { DndTypes, ErrorCode } from 'shared/constants';
import { useAlphamartNavigate } from 'shared/hooks/useAlphamartRouter';
import { useSortNonstandardConverters } from 'shared/mutations';
import { useGetNonstandardConverters } from 'shared/queries';
import { AlphamartHttpError } from 'shared/types';
import { useAppDispatch } from 'store/shared/hooks';
import { hideModal, showModal } from 'store/shared/modal';
import { snackBarPushFailure, snackBarPushSuccess } from 'store/shared/snackBarSlice';
import { MEDIA_QUERY } from 'theme';
import { messages, TypedFormattedMessage, useTypedIntl } from '../locale/messages';

const previewStyles = css`
  width: calc(100% - 72px);
  @media ${MEDIA_QUERY.MD} {
    width: calc(65% - 48px);
  }
`;

const Container = styled.div`
  padding: 20px 24px 20px 24px;
  @media (${MEDIA_QUERY.MD}) {
    padding: 20px 24px;
  }
`;

const Header = styled.div`
  display: flex;
  gap: 12px 24px;
  flex-wrap: wrap;
  align-items: center;
`;

interface Props {
  onUpdate: () => void;
}

const SortNonstandardConvertersComponent = ({ onUpdate }: Props): React.ReactElement => {
  const navigate = useAlphamartNavigate();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const intl = useTypedIntl();
  const [items, setItems] = useState<SortableItem[]>([]);

  const nonstandardConverters = useGetNonstandardConverters(undefined, {
    onSuccess: data => {
      setItems(data.data.map((p, index) => ({ id: p.id, index, content: p.material })));
    },
  });
  const sortNonstandardConverters = useSortNonstandardConverters();

  const isUnchanged =
    nonstandardConverters.isFetching ||
    isEqual(
      items.map(p => p.id),
      nonstandardConverters.data?.data.map(p => p.id),
    );

  const handleSave = async () => {
    const nonstandardConvertersIds = items.map(item => item.id);
    try {
      await sortNonstandardConverters.mutateAsync({ nonstandardConvertersIds });
      onUpdate();
      dispatch(
        snackBarPushSuccess(intl.formatMessage({ id: 'SortNonstandardConverters.Result.Success' })),
      );
      navigate('/nonstandard-converters');
    } catch (error) {
      if (
        (error as AlphamartHttpError)?.response?.data?.errorData?.message ===
        ErrorCode.NONSTANDARD_CONVERTER_INVALID_IDS_IN_RANK
      ) {
        dispatch(
          snackBarPushFailure(
            intl.formatMessage({ id: 'SortNonstandardConverters.Result.Failure' }),
          ),
        );
        return nonstandardConverters.refetch();
      }
      throw error;
    }
  };

  const onBeforeClose = (onClose: () => void): void => {
    if (isUnchanged) return onClose();

    dispatch(
      showModal({
        message: intl.formatMessage({ id: 'SortNonstandardConverters.Modal.Message' }),
        onClose: () => {
          dispatch(hideModal());
        },
        onConfirm: () => {
          dispatch(hideModal());
          return onClose();
        },
      }),
    );
  };

  return (
    <Details
      title={
        <Header>
          <span>
            <TypedFormattedMessage id="SortNonstandardConverters.Header" />
          </span>
          <Button
            onClick={handleSave}
            content="text"
            variant="filled"
            label={intl.formatMessage({ id: 'Global.Save' })}
            disabled={isUnchanged || sortNonstandardConverters.isLoading}
          />
        </Header>
      }
      onBeforeClose={onBeforeClose}
      backUrl="/nonstandard-converters"
      ref={scrollContainerRef}
      swipeToClose={false}
    >
      <LoadableContent
        loading={nonstandardConverters.isFetching || sortNonstandardConverters.isLoading}
        mode={LoadableContent.MODE.FULL}
        drawContent
      >
        <Container>
          <SortableList
            items={items}
            setItems={setItems}
            type={DndTypes.NONSTANDARD_CONVERTERS}
            autoScrollOptions={{ scrollableElement: scrollContainerRef.current?.children[0] }}
            previewClassName={previewStyles}
          />
        </Container>
      </LoadableContent>
    </Details>
  );
};

export const SortNonstandardConverters = withAlphamartIntlProvider(
  SortNonstandardConvertersComponent,
  messages,
);
